import { createContext, useContext, type ReactNode } from 'react';
import type { FlowHydrationManager } from './types';

/**
 * React-контекст для предоставления менеджера гидрации по всему дереву компонентов.
 * @internal
 */
const context = createContext<FlowHydrationManager | null>(null);

/**
 * Props для компонента {@link FlowHydrationProvider}.
 */
export interface FlowHydrationProviderProps {
    /**
     * Экземпляр менеджера гидрации для предоставления дереву компонентов.
     * Используйте {@link createClientHydrationManager} на клиенте или
     * {@link createServerHydrationManager} на сервере.
     */
    manager: FlowHydrationManager;

    /**
     * Дочерние компоненты, которые будут иметь доступ к менеджеру гидрации.
     */
    children: ReactNode;
}

/**
 * Компонент-провайдер, который делает менеджер гидрации доступным для всех дочерних компонентов.
 *
 * Этот компонент должен оборачивать ваше приложение на корневом уровне, чтобы включить функциональность
 * гидрации по всему дереву компонентов. Используйте {@link useHydration} в дочерних
 * компонентах для доступа к менеджеру.
 *
 * ## Использование
 *
 * На клиенте предоставьте клиентский менеджер гидрации:
 * ```tsx
 * import { createClientHydrationManager, FlowHydrationProvider } from '@tsip/flow-react';
 *
 * const manager = createClientHydrationManager();
 *
 * function App() {
 *   return (
 *     <FlowHydrationProvider manager={manager}>
 *       <YourApp />
 *     </FlowHydrationProvider>
 *   );
 * }
 * ```
 *
 * На сервере предоставьте серверный менеджер гидрации:
 * ```tsx
 * import { createServerHydrationManager, FlowHydrationProvider } from '@tsip/flow-react';
 * import { renderToString } from 'react-dom/server';
 *
 * function renderApp() {
 *   const manager = createServerHydrationManager();
 *
 *   const html = renderToString(
 *     <FlowHydrationProvider manager={manager}>
 *       <YourApp />
 *     </FlowHydrationProvider>
 *   );
 *
 *   return { html, script: manager.getScript() };
 * }
 * ```
 *
 * @param props - Props компонента
 * @returns Компонент-провайдер
 */
export function FlowHydrationProvider({ manager, children }: FlowHydrationProviderProps) {
    return <context.Provider value={manager}>{children}</context.Provider>;
}

/**
 * Хук для доступа к менеджеру гидрации из React-контекста.
 *
 * Этот хук получает экземпляр {@link FlowHydrationManager}, предоставленный ближайшим
 * предком {@link FlowHydrationProvider}. Возвращает `null`, если провайдер не найден в
 * дереве компонентов.
 *
 * @returns Экземпляр менеджера гидрации, или `null`, если провайдер не найден
 *
 * @internal
 */
export function useHydration(): FlowHydrationManager | null {
    return useContext(context);
}
